import React from "react";
import { useTranslation } from "react-i18next";
import { StatCard } from "@outpave/ds";
import { Columns } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";
import type { PlatformRewardsPool } from "./types";

interface RewardsPoolSummaryProps {
  pool: PlatformRewardsPool | null;
  loading: boolean;
}

export const isPoolUnderfunded = (pool: PlatformRewardsPool | null): boolean =>
  !!pool &&
  pool.coveragePercent !== null &&
  pool.coverageThresholdPercent > 0 &&
  pool.coveragePercent < pool.coverageThresholdPercent;

const RewardsPoolSummary: React.FC<RewardsPoolSummaryProps> = ({
  pool,
  loading,
}) => {
  const { t } = useTranslation();
  const currency = pool?.currency ?? "usd";
  const money = (value: number | undefined) =>
    formatCashFull(value ?? 0, currency);

  return (
    <Columns gap="var(--space-4)" count={{ xs: 1, md: 3 }}>
      <StatCard
        title={t("Cash in pool")}
        value={money(pool?.cash)}
        tooltip={t("Money the pool holds at the provider")}
        loading={loading}
      />
      <StatCard
        title={t("Redeemable rewards")}
        value={money(pool?.redeemable)}
        tooltip={t("Rewards accounts in the pool could redeem now")}
        loading={loading}
      />
      <StatCard
        title={t("Owed to accounts")}
        value={money(pool?.owed)}
        tooltip={t(
          "What this platform has funded or still owes for its accounts",
        )}
        loading={loading}
      />
    </Columns>
  );
};

export default RewardsPoolSummary;
